import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const run = async (fn, msg, after) => { try { await fn(); toast.success(msg); after(); } catch (err) { toast.error(formatApiError(err)); } };

function SubmitForm({ com, onDone }) {
  const { data: cards = [] } = useQuery({ queryKey: ["me", "cards", "submittable"], queryFn: () => api.get("/me/cards", { params: { page_size: 100 } }).then((r) => r.data.items.filter((c) => c.visibility !== "private" && c.publication_status !== "draft")) });
  const [f, setF] = useState({ card_id: "", note: "" });
  return (
    <form className="panel grid gap-2 p-4" onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/communities/${com.slug}/submissions`, f), "Card submitted for review", () => { setF({ card_id: "", note: "" }); onDone(); }); }} data-testid="submission-form">
      <p className="eyebrow">Submit one of your cards</p>
      <select className="field" value={f.card_id} onChange={(e) => setF({ ...f, card_id: e.target.value })} data-testid="submission-card-select">
        <option value="">Choose a published public or unlisted card…</option>
        {cards.map((c) => <option key={c.card_id} value={c.card_id}>{c.name}</option>)}
      </select>
      <input className="field" placeholder="Note for the reviewers (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="submission-note-input" />
      <button className="btn-gold justify-self-start" disabled={!f.card_id} data-testid="submission-submit-button">Submit</button>
    </form>
  );
}

function ReviewActions({ s, collections, onDone }) {
  const [col, setCol] = useState(collections[0]?.collection_id || "");
  const decide = (decision) => {
    const note = decision === "approve" ? "" : window.prompt(decision === "reject" ? "Reason for rejecting:" : "What should the creator change?", "");
    if (note === null) return;
    run(() => api.post(`/submissions/${s.submission_id}/review`, { decision, note, collection_id: decision === "approve" ? col : null }), "Decision recorded", onDone);
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <select className="field w-auto py-1 text-xs" value={col} onChange={(e) => setCol(e.target.value)} data-testid={`submission-collection-${s.submission_id}`}>
        {collections.map((c) => <option key={c.collection_id} value={c.collection_id}>{c.name}</option>)}</select>
      <button className="btn-gold text-xs" disabled={!col} onClick={() => decide("approve")} data-testid={`submission-approve-${s.submission_id}`}>Approve</button>
      <button className="btn-outline text-xs" onClick={() => decide("request_changes")} data-testid={`submission-changes-${s.submission_id}`}>Request changes</button>
      <button className="btn-outline text-xs text-red-300" onClick={() => decide("reject")} data-testid={`submission-reject-${s.submission_id}`}>Reject</button>
    </div>
  );
}

export function CommunitySubmissions({ com }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const reviewer = com.my_permissions.includes("submissions.review");
  const key = ["community", com.slug, "submissions"];
  const { data = [] } = useQuery({ queryKey: key, enabled: !!user, queryFn: () => api.get(`/communities/${com.slug}/submissions`).then((r) => r.data.items) });
  const { data: collections = [] } = useQuery({ queryKey: ["community", com.slug, "collections"], enabled: reviewer, queryFn: () => api.get(`/communities/${com.slug}/collections`).then((r) => r.data.items) });
  if (!user) return <p className="text-sm text-slate-500">Sign in to submit cards.</p>;
  const done = () => qc.invalidateQueries({ queryKey: key });
  const member = com.my_membership?.status === "active" && com.my_membership.role !== "guest";
  return (
    <div className="space-y-4" data-testid="community-submissions">
      <p className="text-xs text-slate-500">Approval adds a card to one of this community's collections. It is legal only in formats whose card pool uses that collection.</p>
      {member && <SubmitForm com={com} onDone={done} />}
      {data.map((s) => (
        <article key={s.submission_id} className="panel p-4" data-testid={`submission-item-${s.submission_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Link to={`/cards/${s.card_id}`} className="font-heading text-amber-100 hover:underline">{s.card?.name || s.card_id}</Link>
            <span className="font-mono text-[10px] uppercase text-slate-400" data-testid={`submission-status-${s.submission_id}`}>{s.status.replace("_", " ")} · v{s.card_version}</span>
          </div>
          <p className="font-mono text-[10px] text-slate-500">by @{s.submitter?.username}{s.reviewer ? ` · reviewed by @${s.reviewer.username}` : ""}</p>
          {s.decision_note && <p className="mt-1 text-sm text-slate-300">{s.decision_note}</p>}
          {reviewer && s.status === "pending" && s.submitter_id !== user.user_id && <ReviewActions s={s} collections={collections} onDone={done} />}
          {s.submitter_id === user.user_id && ["pending", "changes_requested"].includes(s.status) && <div className="mt-2 flex gap-3 text-xs">
            {s.status === "changes_requested" && <button className="text-amber-300" onClick={() => run(() => api.post(`/submissions/${s.submission_id}/resubmit`, { note: "" }), "Resubmitted", done)} data-testid={`submission-resubmit-${s.submission_id}`}>Resubmit current version</button>}
            <button className="text-red-300" onClick={() => run(() => api.post(`/submissions/${s.submission_id}/withdraw`), "Withdrawn", done)} data-testid={`submission-withdraw-${s.submission_id}`}>Withdraw</button></div>}
        </article>
      ))}
      {!data.length && <p className="text-sm text-slate-500" data-testid="submissions-empty">No submissions yet.</p>}
    </div>
  );
}
